import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';
import { formatGmdAmount, formatMsisdn, splitPremium, toJsDate, useTenantWallets } from '../wallets';
import { useI18n } from '../i18n';
import { moduleOn, useTenantFeatures } from '../tenantFeatures';
import * as XLSX from 'xlsx';

function inMonth(value, year, month) {
    const d = toJsDate(value);
    if (!d) return false;
    return d.getFullYear() === year && d.getMonth() === month;
}

function csvEscape(v) {
    return '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
}

function downloadCsv(filename, rows) {
    const body = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function downloadXlsx(filename, sheets) {
    const wb = XLSX.utils.book_new();
    Object.keys(sheets).forEach((name) => {
        const aoa = sheets[name];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
    });
    XLSX.writeFile(wb, filename);
}


function FinanceReport() {
    const { session, loading } = useCompanySession();
    const { t } = useI18n();
    const wallets = useTenantWallets(session && session.tenantId);
    const features = useTenantFeatures(session && session.tenantId);
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth());
    const [policies, setPolicies] = useState([]);
    const [claims, setClaims] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!session?.tenantId) return;
        const t = session.tenantId;
        Promise.all([
            getDocs(collection(db, 'tenants', t, 'policies')),
            getDocs(collection(db, 'tenants', t, 'claims')),
            getDocs(collection(db, 'tenants', t, 'transactions')),
            getDocs(collection(db, 'tenants', t, 'commissions'))
        ]).then(([p, c, tx, co]) => {
            const pl = []; p.forEach((d) => pl.push({ id: d.id, ...d.data() }));
            const cl = []; c.forEach((d) => cl.push({ id: d.id, ...d.data() }));
            const tl = []; tx.forEach((d) => tl.push({ id: d.id, ...d.data() }));
            const col = []; co.forEach((d) => col.push({ id: d.id, ...d.data() }));
            setPolicies(pl);
            setClaims(cl);
            setTransactions(tl);
            setCommissions(col);
            setReady(true);
        }).catch(() => setReady(true));
    }, [session]);

    const pack = useMemo(() => {
        const written = policies.filter((p) => inMonth(p.effectiveDate || p.createdAt, year, month));
        const lines = written.map((p) => {
            const prem = splitPremium(p);
            const holder = p.policyHolder || {};
            return {
                policyNumber: p.policyNumber || p.id,
                holder: holder.fullName || '',
                type: p.policyType || p.categoryName || '',
                status: p.status || '',
                payment: p.paymentMethod || '',
                nrp: prem.nrp,
                wmf: prem.wmf,
                graTax: prem.graTax,
                gp: prem.grossPremium
            };
        });
        const sum = (key) => lines.reduce((n, r) => n + Number(r[key] || 0), 0);
        const inForce = policies.filter((p) => {
            const s = String(p.status || '').toUpperCase();
            const exp = toJsDate(p.expiryDate);
            return (s === 'ACTIVE' || s === 'APPROVED') && (!exp || exp.getTime() >= Date.now());
        }).length;
        const txMonth = transactions.filter((t) => inMonth(t.paidAt, year, month));
        const byMethod = {};
        txMonth.forEach((t) => {
            const k = String(t.paymentMethod || 'OTHER').toUpperCase();
            byMethod[k] = (byMethod[k] || 0) + Number(t.amountPaidGMD || 0);
        });
        const claimsMonth = claims.filter((c) => inMonth(c.createdAt, year, month));
        const claimsOpen = claims.filter((c) => {
            const s = String(c.status || '').toLowerCase();
            return s !== 'closed' && s !== 'paid' && s !== 'rejected';
        });
        const commMonth = commissions.filter((c) => inMonth(c.createdAt, year, month));
        return {
            lines,
            totals: { nrp: sum('nrp'), wmf: sum('wmf'), gra: sum('graTax'), gp: sum('gp'), count: lines.length },
            inForce,
            byMethod,
            claimsMonth: claimsMonth.length,
            claimsOpen: claimsOpen.length,
            claimsReserve: claimsOpen.reduce((n, c) => n + Number(c.estimatedRepairCost || c.reserveAmount || 0), 0),
            commission: commMonth.reduce((n, c) => n + Number(c.amountGMD || 0), 0),
            commissionRows: commMonth,
            collected: txMonth.reduce((n, t) => n + Number(t.amountPaidGMD || 0), 0)
        };
    }, [policies, claims, transactions, commissions, year, month]);

    const periodKey = year + '-' + String(month + 1).padStart(2, '0');
    const periodLabel = new Date(year, month, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    const lifeLines = pack.lines.filter((r) => String(r.type || '').toUpperCase().indexOf('LIFE') !== -1);
    const nonLifeLines = pack.lines.filter((r) => String(r.type || '').toUpperCase().indexOf('LIFE') === -1);
    const claimsMonthRows = claims.filter((c) => inMonth(c.createdAt, year, month));
    const txMonth = transactions.filter((t) => inMonth(t.paidAt, year, month));

    const formCRows = [
        ['Central Bank of The Gambia — Insurance Regulations 2005'],
        ['Form C — Certificate / life insurance return (working pack from Pulse book)'],
        ['Insurer', session && session.companyName],
        ['Period', periodLabel],
        ['Currency', 'GMD'],
        [],
        ['Policy number', 'Holder', 'Status', 'NRP', 'WMF', 'GRA tax', 'Gross premium'],
        ...lifeLines.map((r) => [r.policyNumber, r.holder, r.status, r.nrp, r.wmf, r.graTax, r.gp]),
        [],
        ['Life policies written', lifeLines.length],
        ['Life gross premium', lifeLines.reduce((n, r) => n + Number(r.gp || 0), 0)],
        ['Note', 'Official Form C is the life solvency certificate. This pack lists life written premium from the tenant book for the officer to file.']
    ];

    const formDRows = [
        ['Central Bank of The Gambia — Insurance Regulations 2005'],
        ['Form D — Certificate / non-life insurance return (working pack from Pulse book)'],
        ['Insurer', session && session.companyName],
        ['Period', periodLabel],
        ['Currency', 'GMD'],
        [],
        ['Policy number', 'Holder', 'Class', 'Status', 'NRP', 'WMF', 'GRA tax', 'Gross premium'],
        ...nonLifeLines.map((r) => [r.policyNumber, r.holder, r.type, r.status, r.nrp, r.wmf, r.graTax, r.gp]),
        [],
        ['Non-life written', nonLifeLines.length],
        ['Non-life gross premium', nonLifeLines.reduce((n, r) => n + Number(r.gp || 0), 0)],
        ['Open claims (all)', pack.claimsOpen],
        ['Open claims reserve', pack.claimsReserve],
        ['Note', 'Official Form D is the non-life solvency certificate. Solvency margin uses written premium (GP less refunds). Assets/liabilities from statutory accounts are not stored in Pulse.']
    ];

    const formERows = [
        ['Central Bank of The Gambia — Insurance Regulations 2005'],
        ['Form E — Returns and accounts / collections (working pack)'],
        ['Insurer', session && session.companyName],
        ['Period', periodLabel],
        [],
        ['Written NRP', pack.totals.nrp],
        ['WMF 10% (wakala)', pack.totals.wmf],
        ['GRA premium tax', pack.totals.gra],
        ['Gross written premium', pack.totals.gp],
        ['Collected this month', pack.collected],
        [],
        ['Collections by rail'],
        ['Rail', 'Amount GMD'],
        ...Object.keys(pack.byMethod).map((k) => [k, pack.byMethod[k]]),
        [],
        ['Receipt / txn', 'Policy', 'Rail', 'Amount', 'Paid at'],
        ...txMonth.map((t) => [t.receiptNo || t.id, t.policyNumber, t.paymentMethod, t.amountPaidGMD, t.paidAt])
    ];

    const formFRows = [
        ['Central Bank of The Gambia — Insurance Regulations 2005'],
        ['Form F — Intermediaries / commission return (working pack)'],
        ['Insurer', session && session.companyName],
        ['Period', periodLabel],
        [],
        ['Broker', 'Policy', 'Premium GMD', 'Rate', 'Commission GMD', 'Status'],
        ...pack.commissionRows.map((c) => [c.brokerEmail, c.policyNumber, c.premiumGMD, c.rate, c.amountGMD, c.status]),
        [],
        ['Commission accrued', pack.commission],
        ['Note', 'List of agents/brokers to whom commission is payable (Reg. registration file). Pulse stores broker email and 10% default.']
    ];

    const itasRows = [
        ['Gambia Revenue Authority — ITAS-style premium tax pack'],
        ['Taxpayer / insurer', session && session.companyName],
        ['Period', periodLabel],
        ['Currency', 'GMD'],
        [],
        ['Tax line', 'Base', 'Amount GMD'],
        ['GRA premium tax', 'Written premium tax component', pack.totals.gra],
        ['WMF (wakala management fee)', '10% of NRP — not GRA but listed for recon', pack.totals.wmf],
        ['Net risk premium', 'NRP', pack.totals.nrp],
        ['Gross premium', 'GP = NRP + WMF + GRA', pack.totals.gp],
        ['Collected (cash/wallets)', 'Receipts in month', pack.collected],
        [],
        ['Policy', 'Holder', 'Class', 'NRP', 'WMF', 'GRA', 'GP'],
        ...pack.lines.map((r) => [r.policyNumber, r.holder, r.type, r.nrp, r.wmf, r.graTax, r.gp]),
        [],
        ['Note', 'Not an official GRA XML schema. CSV/Excel for the finance officer to upload or rekey into ITAS.']
    ];

    const claimsPack = [
        ['Claims in period', periodLabel],
        ['Claim', 'Holder', 'Policy', 'Status', 'Reserve', 'Filed'],
        ...claimsMonthRows.map((c) => [c.claimNumber || c.id, c.clientName, c.policyNumber, c.status, c.estimatedRepairCost || c.reserveAmount || 0, c.createdAt])
    ];

    const exportCsv = () => {
        const header = ['Policy', 'Holder', 'Type', 'Status', 'Payment', 'NRP', 'WMF', 'GRA', 'Gross premium'];
        const rows = [header, ...pack.lines.map((r) => [r.policyNumber, r.holder, r.type, r.status, r.payment, r.nrp, r.wmf, r.graTax, r.gp])];
        rows.push([]);
        rows.push(['TOTALS', '', '', '', '', pack.totals.nrp, pack.totals.wmf, pack.totals.gra, pack.totals.gp]);
        downloadCsv('pulse-wmf-gra-' + periodKey + '.csv', rows);
    };

    const exportForm = (letter) => {
        const map = { C: formCRows, D: formDRows, E: formERows, F: formFRows };
        downloadCsv('CBG-Form-' + letter + '-' + periodKey + '.csv', map[letter]);
        downloadXlsx('CBG-Form-' + letter + '-' + periodKey + '.xlsx', { ['Form ' + letter]: map[letter], Claims: claimsPack });
    };

    const exportItas = () => {
        downloadCsv('GRA-ITAS-' + periodKey + '.csv', itasRows);
        downloadXlsx('GRA-ITAS-' + periodKey + '.xlsx', { ITAS: itasRows, Written: [['Policy', 'Holder', 'Type', 'NRP', 'WMF', 'GRA', 'GP'], ...pack.lines.map((r) => [r.policyNumber, r.holder, r.type, r.nrp, r.wmf, r.graTax, r.gp])] });
    };

    if (loading || !session || !ready) return <div className="p-5">{t('gra.loading')}</div>;
    if (!moduleOn(features, 'graReturns')) {
        return (
            <CompanyLayout session={session}>
                <div className="alert alert-warning">{t('modules.disabled')}</div>
            </CompanyLayout>
        );
    }
    if (!['admin', 'finance', 'audit', 'manager'].includes(session.role)) {
        return (
            <CompanyLayout session={session}>
                <div className="alert alert-warning">{t('gra.restricted')}</div>
            </CompanyLayout>
        );
    }

    const label = new Date(year, month, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    return (
        <CompanyLayout session={session}>
            <p className="text-muted">{t('gra.intro', { company: session.companyName, wave: formatMsisdn(wallets.waveNumber) })}</p>
            <p className="small text-muted">{t('gra.cbgNote')}</p>
            <div className="d-flex gap-2 flex-wrap mb-3">
                <select className="form-select" style={{ maxWidth: 160 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    {Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{new Date(2000, i, 1).toLocaleString('en-GB', { month: 'long' })}</option>)}
                </select>
                <input type="number" className="form-control" style={{ maxWidth: 120 }} value={year} onChange={(e) => setYear(Number(e.target.value))} />
                <button type="button" className="bd-btn bd-btn-secondary" onClick={exportCsv}>{t('gra.downloadCsv')}</button>
                <button type="button" className="bd-btn bd-btn-primary" onClick={() => window.print()}>{t('gra.printPack')}</button>
            </div>
            <div className="d-flex gap-2 flex-wrap mb-3">
                <button type="button" className="bd-btn bd-btn-secondary" onClick={() => exportForm('C')}>{t('gra.formC')}</button>
                <button type="button" className="bd-btn bd-btn-secondary" onClick={() => exportForm('D')}>{t('gra.formD')}</button>
                <button type="button" className="bd-btn bd-btn-secondary" onClick={() => exportForm('E')}>{t('gra.formE')}</button>
                <button type="button" className="bd-btn bd-btn-secondary" onClick={() => exportForm('F')}>{t('gra.formF')}</button>
                <button type="button" className="bd-btn bd-btn-success" onClick={exportItas}>{t('gra.itas')}</button>
            </div>
            <div className="bd-live-stats mb-3">
                <div className="bd-stat-card"><div className="bd-label">{t('gra.pulsePay')}</div><div className="bd-value">{formatGmdAmount(pack.byMethod.PULSEPAY || pack.byMethod.PULSE_PAY || 0)}</div></div>
                <div className="bd-stat-card"><div className="bd-label">{t('gra.claimsMonth')}</div><div className="bd-value">{pack.claimsMonth}</div></div>
            </div>

            <div className="bd-live-stats mb-3">
                <div className="bd-stat-card"><div className="bd-label">Policies written</div><div className="bd-value">{pack.totals.count}</div></div>
                <div className="bd-stat-card"><div className="bd-label">NRP</div><div className="bd-value">{formatGmdAmount(pack.totals.nrp)}</div></div>
                <div className="bd-stat-card"><div className="bd-label">WMF 10%</div><div className="bd-value">{formatGmdAmount(pack.totals.wmf)}</div></div>
                <div className="bd-stat-card"><div className="bd-label">GRA tax</div><div className="bd-value">{formatGmdAmount(pack.totals.gra)}</div></div>
                <div className="bd-stat-card"><div className="bd-label">Gross premium</div><div className="bd-value green">{formatGmdAmount(pack.totals.gp)}</div></div>
            </div>
            <div className="bd-live-stats mb-4">
                <div className="bd-stat-card"><div className="bd-label">In force now</div><div className="bd-value">{pack.inForce}</div></div>
                <div className="bd-stat-card"><div className="bd-label">Collected ({label})</div><div className="bd-value">{formatGmdAmount(pack.collected)}</div></div>
                <div className="bd-stat-card"><div className="bd-label">Wave</div><div className="bd-value">{formatGmdAmount(pack.byMethod.WAVE || 0)}</div></div>
                <div className="bd-stat-card"><div className="bd-label">Cash</div><div className="bd-value">{formatGmdAmount(pack.byMethod.CASH || 0)}</div></div>
                <div className="bd-stat-card"><div className="bd-label">Broker commission</div><div className="bd-value">{formatGmdAmount(pack.commission)}</div></div>
                <div className="bd-stat-card"><div className="bd-label">Open claims reserve</div><div className="bd-value">{formatGmdAmount(pack.claimsReserve)}</div></div>
            </div>

            <div className="bd-card">
                <h5>Written premium — {label}</h5>
                <div className="table-responsive">
                    <table className="bd-table mb-0">
                        <thead>
                            <tr><th>Policy</th><th>Holder</th><th>Type</th><th>NRP</th><th>WMF</th><th>GRA</th><th>GP</th><th>Pay</th></tr>
                        </thead>
                        <tbody>
                            {pack.lines.map((r) => (
                                <tr key={r.policyNumber}>
                                    <td>{r.policyNumber}</td>
                                    <td>{r.holder}</td>
                                    <td>{r.type}</td>
                                    <td>{r.nrp.toLocaleString()}</td>
                                    <td>{r.wmf.toLocaleString()}</td>
                                    <td>{r.graTax.toLocaleString()}</td>
                                    <td>{r.gp.toLocaleString()}</td>
                                    <td>{r.payment}</td>
                                </tr>
                            ))}
                            {pack.lines.length === 0 && <tr><td colSpan={8} className="text-muted">No policies with an effective date in this month.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {pack.commissionRows.length > 0 && (
                <div className="bd-card mt-3">
                    <h5>Broker commission accrued</h5>
                    <table className="bd-table mb-0">
                        <thead><tr><th>Broker</th><th>Policy</th><th>Premium</th><th>Rate</th><th>Commission</th></tr></thead>
                        <tbody>
                            {pack.commissionRows.map((c) => (
                                <tr key={c.id}>
                                    <td>{c.brokerEmail}</td>
                                    <td>{c.policyNumber}</td>
                                    <td>{formatGmdAmount(c.premiumGMD)}</td>
                                    <td>{Math.round((c.rate || 0) * 100)}%</td>
                                    <td>{formatGmdAmount(c.amountGMD)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </CompanyLayout>
    );
}

export default FinanceReport;
